/**
 * Halamama Wishlist — Poco Theme Native
 * localStorage-based, AJAX, no page reloads, cross-tab BroadcastChannel sync
 */

if (!window.__halamamaWishlistLoaded) {
  window.__halamamaWishlistLoaded = true;

  /* ─── Storage ─────────────────────────────────────────────────────── */
  const WishlistStorage = (() => {
    const KEY = 'halamama_wishlist';

    function _get() {
      try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
    }
    function _save(list) {
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch {}
    }
    function getAll() { return _get(); }
    function has(productId) {
      return _get().some(p => String(p.id) === String(productId));
    }
    function add(product) {
      const list = _get();
      if (!list.some(p => String(p.id) === String(product.id))) {
        list.push(product);
        _save(list);
      }
      return list;
    }
    function remove(productId) {
      const list = _get().filter(p => String(p.id) !== String(productId));
      _save(list);
      return list;
    }
    function count() { return _get().length; }

    return { getAll, has, add, remove, count };
  })();

  /* ─── UI ──────────────────────────────────────────────────────────── */
  const WishlistUI = (() => {
    let _channel = null;

    function _broadcast() {
      if (_channel) try { _channel.postMessage('sync'); } catch {}
    }

    function initChannel() {
      if (typeof BroadcastChannel === 'undefined') return;
      _channel = new BroadcastChannel('halamama_wl_sync');
      _channel.onmessage = () => { updateAllButtons(); updateBadge(); };
    }

    function updateAllButtons() {
      document.querySelectorAll('[data-wl-btn]').forEach(btn => {
        const id = btn.dataset.productId;
        const active = WishlistStorage.has(id);
        btn.classList.toggle('wl-active', active);
        btn.setAttribute('aria-label', active ? 'Remove from Wishlist' : 'Add to Wishlist');
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    function updateBadge() {
      const c = WishlistStorage.count();
      document.querySelectorAll('.wl-count-badge').forEach(el => {
        el.textContent = c;
        el.style.display = c > 0 ? 'flex' : 'none';
      });
    }

    function _extractProductData(btn) {
      return {
        id:           btn.dataset.productId,
        handle:       btn.dataset.handle       || '',
        title:        btn.dataset.title        || '',
        image:        btn.dataset.image        || '',
        price:        btn.dataset.price        || '',
        variantId:    btn.dataset.variantId    || '',
        variantTitle: btn.dataset.variantTitle || '',
      };
    }

    function toggle(btn) {
      const id = btn.dataset.productId;
      if (WishlistStorage.has(id)) {
        WishlistStorage.remove(id);
      } else {
        WishlistStorage.add(_extractProductData(btn));
        // pop animation
        btn.classList.add('wl-pop');
        btn.addEventListener('animationend', () => btn.classList.remove('wl-pop'), { once: true });
      }
      updateAllButtons();
      updateBadge();
      _broadcast();

      // if on wishlist page, re-render
      if (document.getElementById('wl-page-root')) WishlistPage.render();
    }

    function init() {
      initChannel();
      updateAllButtons();
      updateBadge();

      document.addEventListener('click', e => {
        const btn = e.target.closest('[data-wl-btn]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        toggle(btn);
      });
    }

    return { init, updateAllButtons, updateBadge };
  })();

  /* ─── Cart Helper (shared ATC logic) ─────────────────────────────── */
  function _addToCartAjax(variantId, btn, afterCallback) {
    btn.disabled = true;
    const orig = btn.innerHTML;
    btn.innerHTML = '<span class="wl-spinner"></span>';

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body:    JSON.stringify({ id: variantId, quantity: 1 }),
    })
    .then(r => {
      if (!r.ok) {
        const e = new Error('add failed');
        e.soldOut = r.status === 422;
        throw e;
      }
      return r.json();
    })
    .then(() => {
      btn.innerHTML = '✓ Added';
      setTimeout(() => { btn.innerHTML = orig; btn.disabled = false; }, 1600);
      if (afterCallback) afterCallback();
      _refreshCartDrawer();
    })
    .catch(err => {
      /* A sold-out variant answers 422. The button used to simply reset, so the
         click looked like it had done nothing at all. */
      btn.innerHTML = orig;
      btn.disabled = false;

      var card = btn.closest('.wl-sl-card, .wl-page-card, .card-wrapper, .wl-card') || btn.parentElement;
      if (err && err.soldOut) {
        _markSoldOut(card, btn);
      } else {
        _flash(btn, 'Could not add');
      }
    });
  }

  /* Brief, in-place feedback. The wishlist has no toast of its own and the
     cart drawer does not open on failure, so the button itself has to say it. */
  function _flash(btn, text) {
    const orig = btn.innerHTML;
    btn.classList.add('wl-btn--err');
    btn.innerHTML = _esc(text);
    setTimeout(() => {
      btn.classList.remove('wl-btn--err');
      btn.innerHTML = orig;
    }, 2200);
  }

  /* Swaps a card into its sold-out state: label on the media, Add to Cart
     replaced by Notify me pointing at the product page, where the Klaviyo
     Back in Stock form is already wired to the same list. */
  function _markSoldOut(card, btn) {
    if (!card) return;
    card.classList.add('wl-oos');

    if (!card.querySelector('.wl-oos-badge')) {
      const media = card.querySelector('.card__inner, .wl-sl-media, .wl-page-media') || card;
      const badge = document.createElement('span');
      badge.className = 'wl-oos-badge';
      badge.textContent = 'Out of Stock';
      media.appendChild(badge);
    }

    if (btn && btn.parentNode && !card.querySelector('.wl-notify')) {
      const handle = btn.getAttribute('data-handle') ||
        (card.querySelector('a[href*="/products/"]') || {}).pathname || '';
      const href = handle
        ? (handle.indexOf('/') === 0 ? handle : '/products/' + handle) + '?notify=1'
        : '#';

      const a = document.createElement('a');
      a.className = btn.className + ' wl-notify';
      a.href = href;
      a.textContent = 'Notify me';
      btn.parentNode.replaceChild(a, btn);
    }
  }

  /* Availability is not stored in the wishlist — the list is a localStorage
     snapshot taken when the heart was clicked, so an item can sell out long
     after. Each card is checked against the live product JSON once per render,
     with the answers cached for the session. */
  const _availCache = Object.create(null);

  function _checkAvailability(root) {
    if (!root) return;

    root.querySelectorAll('[data-wl-sl-atc], [data-wl-pg-atc]').forEach(btn => {
      const vid = String(btn.dataset.variantId || '');
      const link = btn.closest('.wl-sl-card, .wl-page-card, .card-wrapper, .wl-card, .grid__item');
      const anchor = link ? link.querySelector('a[href*="/products/"]') : null;
      const handle = btn.dataset.handle ||
        (anchor ? anchor.getAttribute('href').split('?')[0].replace('/products/', '') : '');
      if (!vid || !handle) return;

      btn.setAttribute('data-handle', handle);

      const apply = data => {
        const v = (data.variants || []).find(x => String(x.id) === vid);
        if (v && v.available === false) _markSoldOut(link || btn.parentElement, btn);
      };

      if (_availCache[handle]) {
        _availCache[handle].then(apply).catch(() => {});
        return;
      }

      _availCache[handle] = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then(r => (r.ok ? r.json() : Promise.reject()))
        .catch(() => ({ variants: [] }));

      _availCache[handle].then(apply).catch(() => {});
    });
  }

  function _refreshCartDrawer() {
    fetch('/?sections=cart-drawer,cart-icon-bubble,cart-icon-quick')
      .then(r => r.json())
      .then(sections => {
        const parser = new DOMParser();
        const cartDrawerEl = document.querySelector('cart-drawer');
        const drawerInner  = document.getElementById('CartDrawer');

        // Update cart drawer HTML
        if (drawerInner && sections['cart-drawer']) {
          const doc       = parser.parseFromString(sections['cart-drawer'], 'text/html');
          const newInner  = doc.querySelector('#CartDrawer');
          const newDrawer = doc.querySelector('cart-drawer');
          if (newInner) {
            drawerInner.innerHTML = newInner.innerHTML;
            if (cartDrawerEl && newDrawer) {
              cartDrawerEl.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
            }
            const overlay = document.getElementById('CartDrawer-Overlay');
            if (overlay && cartDrawerEl) {
              overlay.addEventListener('click', cartDrawerEl.close.bind(cartDrawerEl));
            }
          }
        }

        // Update cart icon bubble (header count)
        if (sections['cart-icon-bubble']) {
          const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
          const newBubble = doc.querySelector('#cart-icon-bubble');
          if (newBubble) {
            document.querySelectorAll('#cart-icon-bubble').forEach(el => {
              el.innerHTML = newBubble.innerHTML;
            });
          }
        }

        // Also update cart-icon-quick if present
        if (sections['cart-icon-quick']) {
          const doc = parser.parseFromString(sections['cart-icon-quick'], 'text/html');
          const newQuick = doc.querySelector('#cart-icon-quick');
          if (newQuick) {
            document.querySelectorAll('#cart-icon-quick').forEach(el => {
              el.innerHTML = newQuick.innerHTML;
            });
          }
        }

        // Dispatch cart events so Poco's own listeners update (e.g. sticky bar count)
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:update',  { bubbles: true }));

        if (cartDrawerEl) cartDrawerEl.open();
      })
      .catch(() => {
        // Fallback: fetch /cart.js and patch count bubbles directly
        fetch('/cart.js')
          .then(r => r.json())
          .then(cart => {
            const count = cart.item_count || 0;
            document.querySelectorAll('.cart-count-bubble').forEach(el => {
              const span = el.querySelector('span[aria-hidden]');
              if (span) span.textContent = count;
            });
          })
          .catch(() => {});
      });
  }

  /* ─── Wishlist Page ──────────────────────────────────────────────── */
  const WishlistPage = (() => {
    function render() {
      const root = document.getElementById('wl-page-root');
      if (!root) return;

      const list = WishlistStorage.getAll();

      if (list.length === 0) {
        root.innerHTML = `
          <div class="wl-empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
            <h2>Your wishlist is empty</h2>
            <p>Save products you love and find them here anytime.</p>
            <a href="/collections/all" class="wl-page-btn wl-page-btn--primary">Continue Shopping</a>
          </div>`;
        return;
      }

      let html = `<div class="wl-page-grid">`;
      list.forEach(p => {
        const img = p.image || '';
        const vt  = (p.variantTitle && p.variantTitle !== 'Default Title') ? `<p class="wl-pg-variant">${p.variantTitle}</p>` : '';
        html += `
          <div class="wl-pg-item" data-wl-pg-id="${_esc(String(p.id))}">
            <div class="wl-pg-item__img-wrap">
              <a href="/products/${_esc(p.handle)}">
                <img src="${_esc(img)}" alt="${_esc(p.title)}" loading="lazy" width="260" height="260">
              </a>
            </div>
            <div class="wl-pg-item__body">
              <a href="/products/${_esc(p.handle)}" class="wl-pg-item__title">${_esc(p.title)}</a>
              ${vt}
              <p class="wl-pg-item__price" data-wl-price data-handle="${_esc(p.handle)}" data-variant="${_esc(String(p.variantId || ''))}">${_esc(p.price)}</p>
              <div class="wl-pg-item__actions">
                <button class="wl-page-btn wl-page-btn--atc" data-wl-pg-atc data-variant-id="${_esc(String(p.variantId))}">Add to Cart</button>
                <button class="wl-page-btn wl-page-btn--remove" data-wl-pg-remove data-product-id="${_esc(String(p.id))}" aria-label="Remove">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  Remove
                </button>
              </div>
            </div>
          </div>`;
      });
      html += `</div>
        <div class="wl-page-footer">
          <a href="/collections/all" class="wl-page-btn wl-page-btn--outline">Continue Shopping</a>
        </div>`;
      root.innerHTML = html;

      _checkAvailability(root);

      root.querySelectorAll('[data-wl-pg-atc]').forEach(btn => {
        btn.addEventListener('click', () => _addToCartAjax(btn.dataset.variantId, btn));
      });

      root.querySelectorAll('[data-wl-pg-remove]').forEach(btn => {
        btn.addEventListener('click', () => {
          const id   = btn.dataset.productId;
          const item = root.querySelector(`[data-wl-pg-id="${id}"]`);
          if (item) { item.style.opacity = '0'; item.style.transform = 'scale(0.9)'; }
          setTimeout(() => {
            WishlistStorage.remove(id);
            WishlistUI.updateBadge();
            WishlistUI.updateAllButtons();
            render();
          }, 250);
        });
      });
    }

    return { render };
  })();

  /* ─── Cart Carousel ─────────────────────────────────────────────── */
  /* Desktop: JS infinite carousel  |  Mobile: CSS scroll + snap      */
  const WishlistCartCarousel = (() => {
    let _idx       = 0;
    let _busy      = false;
    let _list      = [];
    let _cols      = 4;
    let _colsD     = 4;
    let _colsM     = 2;
    let _clonesN   = 4;   // Math.ceil(_cols) — head/tail clone count for infinite
    let _track     = null;
    let _viewport  = null;
    let _resizeT   = null;

    function _getCols()  { return window.innerWidth < 750 ? _colsM : _colsD; }
    function _isMobile() { return window.innerWidth < 750; }
    function _iw()       { return _viewport ? _viewport.offsetWidth / _cols : 0; }

    function _goTo(idx, animate) {
      if (!_track) return;
      _track.style.transition = animate ? 'transform 0.38s ease' : 'none';
      _track.style.transform  = `translateX(${-(_clonesN + idx) * _iw()}px)`;
    }

    function _card(p, active) {
      /* Uses the theme's product-card classes so this carousel inherits the
         same look as featured-collection (card--standard / card__inner etc.). */
      const variant = (p.variantTitle && p.variantTitle !== 'Default Title')
        ? `<div class="wl-sl-variant">${_esc(p.variantTitle)}</div>` : '';
      const vendor = p.vendor ? `<div class="card_vendor card-info-vendor">${_esc(p.vendor)}</div>` : '';

      return `
        <div class="wl-sl-item">
          <div class="card-wrapper product-card-wrapper underline-links-hover wl-sl-card">
            <div class="card card--standard card--media">
              <div class="card__inner ratio" style="--ratio-percent: 100%;">
                <div class="card__media">
                  <div class="media media--transparent media--hover-effect">
                    <a href="/products/${_esc(p.handle)}" tabindex="-1" aria-hidden="true">
                      <img src="${_esc(p.image)}" alt="${_esc(p.title)}" loading="lazy"
                           width="300" height="300" class="img-fluid mx-auto">
                    </a>
                  </div>
                </div>
                <button class="wl-heart-btn ${active ? 'wl-active' : ''}"
                  data-wl-btn
                  data-product-id="${_esc(String(p.id))}"
                  data-handle="${_esc(p.handle)}"
                  data-title="${_esc(p.title)}"
                  data-image="${_esc(p.image)}"
                  data-price="${_esc(p.price)}"
                  data-variant-id="${_esc(String(p.variantId))}"
                  data-variant-title="${_esc(p.variantTitle || '')}"
                  aria-label="${active ? 'Remove from Wishlist' : 'Add to Wishlist'}"
                  type="button">
                  <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="0" width="16" height="16" aria-hidden="true">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </button>
              </div>
              <div class="card__content">
                <div class="card-information card-info-redesign">
                  ${vendor}
                  <h3 class="card__heading">
                    <a href="/products/${_esc(p.handle)}" class="full-unstyled-link wl-sl-title">${_esc(p.title)}</a>
                  </h3>
                  ${variant}
                  <div class="card-info-bottom">
                    <div class="card-price-block">
                      <div class="price wl-sl-price" data-wl-price data-handle="${_esc(p.handle)}" data-variant="${_esc(String(p.variantId || ''))}">
                        <span class="price-item price-item--regular">${_esc(p.price)}</span>
                      </div>
                    </div>
                  </div>
                  <button class="wl-sl-atc" data-wl-sl-atc data-variant-id="${_esc(String(p.variantId))}" data-product-id="${_esc(String(p.id))}">Add to Cart</button>
                </div>
              </div>
            </div>
          </div>
        </div>`;
    }

    function render() {
      const root = document.getElementById('wl-cart-carousel-root');
      if (!root) return;

      _colsD   = parseFloat(root.dataset.colsDesktop) || 4;
      _colsM   = parseFloat(root.dataset.colsMobile)  || 2;
      _cols    = _getCols();
      _idx     = 0;
      _busy    = false;
      _track   = null;
      _viewport= null;

      const maxItems = parseInt(root.dataset.maxItems, 10) || 10;
      const section  = root.closest('.wl-cart-section');
      _list = WishlistStorage.getAll().slice(0, maxItems);

      if (_list.length === 0) { if (section) section.style.display = 'none'; return; }
      if (section) section.style.display = '';

      /* Clone count must never exceed the list, or slice() returns fewer items
         than the loop expects and the track ends up with blank slots. */
      _clonesN = Math.min(Math.ceil(_cols), _list.length);

      if (_isMobile()) {
        /* ── Mobile: native CSS scroll + snap, no cloning, no buttons ── */
        root.innerHTML = `
          <div class="wl-sl-outer wl-sl-outer--mobile">
            <div class="wl-sl-viewport wl-sl-viewport--scroll">
              <div class="wl-sl-track wl-sl-track--scroll" style="--wl-cols:${_cols};">
                ${_list.map(p => _card(p, WishlistStorage.has(p.id))).join('')}
              </div>
            </div>
          </div>`;

        // Pixel item width so fractional cols (2.5 etc.) work correctly
        const sv = root.querySelector('.wl-sl-viewport--scroll');
        const st = root.querySelector('.wl-sl-track--scroll');
        st.style.setProperty('--wl-item-w', `${sv.offsetWidth / _cols}px`);

      } else if (_list.length <= _cols) {
        /* Fewer items than visible columns — render a static row. Looping here
           would scroll past the end and leave the track blank. */
        root.innerHTML = `
          <div class="wl-sl-outer wl-sl-outer--static">
            <div class="wl-sl-viewport">
              <div class="wl-sl-track" style="--wl-cols:${_cols};">
                ${_list.map(p => _card(p, WishlistStorage.has(p.id))).join('')}
              </div>
            </div>
          </div>`;

      } else {
        /* ── Desktop: JS infinite carousel ── */
        const full = [..._list.slice(-_clonesN), ..._list, ..._list.slice(0, _clonesN)];

        root.innerHTML = `
          <div class="wl-sl-outer">
            <button class="wl-sl-btn wl-sl-btn--prev" aria-label="Previous" type="button">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="20" height="20"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <div class="wl-sl-viewport">
              <div class="wl-sl-track" style="--wl-cols:${_cols};">
                ${full.map(p => _card(p, WishlistStorage.has(p.id))).join('')}
              </div>
            </div>
            <button class="wl-sl-btn wl-sl-btn--next" aria-label="Next" type="button">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="20" height="20"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>`;

        _track   = root.querySelector('.wl-sl-track');
        _viewport= root.querySelector('.wl-sl-viewport');
        const prev = root.querySelector('.wl-sl-btn--prev');
        const next = root.querySelector('.wl-sl-btn--next');

        root.querySelector('.wl-sl-outer').style.setProperty('--wl-item-width', `${_iw()}px`);
        _goTo(0, false);

        _track.addEventListener('transitionend', () => {
          _busy = false;
          if (_idx >= _list.length) { _idx = 0; _goTo(0, false); }
          else if (_idx < 0)        { _idx = _list.length - 1; _goTo(_idx, false); }
        });

        next.addEventListener('click', () => { if (_busy) return; _busy = true; _goTo(++_idx, true); });
        prev.addEventListener('click', () => { if (_busy) return; _busy = true; _goTo(--_idx, true); });
      }

      _checkAvailability(root);

      /* ── ATC buttons (both modes) ── */
      root.querySelectorAll('[data-wl-sl-atc]').forEach(btn => {
        btn.addEventListener('click', () => {
          const vid = btn.dataset.variantId;
          const pid = btn.dataset.productId;
          _addToCartAjax(vid, btn, () => {
            if (pid) {
              WishlistStorage.remove(pid);
              WishlistUI.updateBadge();
              WishlistUI.updateAllButtons();
            }
            if (window.location.pathname.startsWith('/cart')) {
              setTimeout(() => window.location.reload(), 900);
            } else {
              render();
            }
          });
        });
      });

      window.removeEventListener('resize', _onResize);
      window.addEventListener('resize', _onResize);
    }

    function _onResize() { clearTimeout(_resizeT); _resizeT = setTimeout(render, 250); }

    return { render };
  })();

  /* ─── Escape helper ───────────────────────────────────────────────── */
  function _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  const WishlistPrice = (() => {
    const cache = {};

    function formatFrom(sample) {
      const txt = String(sample || '').trim();
      const m = txt.match(/\d[\d.,\s']*/);
      if (!m) return '';
      const num = m[0].trim();
      const dec = /[.,]\d{2}$/.test(num);
      const key = dec ? (/,\d{2}$/.test(num) ? '{{amount_with_comma_separator}}' : '{{amount}}') : '{{amount_no_decimals}}';
      return txt.replace(num, key);
    }

    function fmt(cents, sample) {
      const format = window.pocoMoneyFormat || formatFrom(sample) || '{{amount}}';
      const n = Number(cents) / 100;
      const parts = (dec, th, ds) => {
        const fixed = n.toFixed(dec).split('.');
        const whole = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
        return dec ? whole + ds + fixed[1] : whole;
      };
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
        if (key === 'amount_no_decimals') return parts(0, ',', '.');
        if (key === 'amount_with_comma_separator') return parts(2, '.', ',');
        if (key === 'amount_no_decimals_with_comma_separator') return parts(0, '.', ',');
        if (key === 'amount_with_apostrophe_separator') return parts(2, "'", '.');
        return parts(2, ',', '.');
      });
    }

    function load(handle) {
      if (!cache[handle]) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        cache[handle] = fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
          .then(r => (r.ok ? r.json() : null))
          .catch(() => null);
      }
      return cache[handle];
    }

    function paint(el, product) {
      const v = product && product.variants
        ? (product.variants.find(x => String(x.id) === el.getAttribute('data-variant')) || product.variants.find(x => x.available) || product.variants[0])
        : null;
      if (!v) {
        el.classList.add('wl-price-fallback');
        return;
      }
      const price = v.price;
      const compare = v.compare_at_price || 0;
      const sample = el.getAttribute('data-wl-sample') || '';
      let html = '<span class="wl-price__now' + (compare > price ? ' is-sale' : '') + '">' + _esc(fmt(price, sample)) + '</span>';
      if (compare > price) {
        const off = Math.round((compare - price) * 100 / compare);
        html += '<s class="wl-price__was">' + _esc(fmt(compare, sample)) + '</s>';
        if (off > 0) html += '<span class="wl-price__off">-' + off + '%</span>';
      }
      el.innerHTML = html;
      el.classList.add('wl-price');
    }

    function localize(scope) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      if (root === '/') return;
      (scope || document).querySelectorAll('a.wl-pg-item__title, a.wl-sl-title, a.wlc__name, .wl-pg-item a[href^="/products/"], .wl-sl-card a[href^="/products/"], .wlc a[href^="/products/"]').forEach(a => {
        if (a.hasAttribute('data-wl-loc')) return;
        const href = a.getAttribute('href') || '';
        if (href.indexOf('/products/') !== 0) return;
        a.setAttribute('data-wl-loc', '');
        const handle = decodeURIComponent(href.split('?')[0].replace('/products/', ''));
        a.setAttribute('href', root + href.slice(1));
        if (!a.textContent.trim()) return;
        load(handle).then(p => {
          if (!p || !p.title) return;
          if (a.querySelector('img')) return;
          a.textContent = p.title;
        });
      });
    }

    function hydrate(scope) {
      (scope || document).querySelectorAll('[data-wl-price]:not([data-wl-price-done])').forEach(el => {
        const handle = el.getAttribute('data-handle');
        if (!handle) return;
        el.setAttribute('data-wl-price-done', '');
        el.setAttribute('data-wl-sample', el.textContent.trim());
        load(handle).then(p => paint(el, p));
      });
    }

    let queued = false;
    function schedule() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; hydrate(); localize(); });
    }

    if ('MutationObserver' in window) {
      new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
    }

    return { hydrate, schedule, localize };
  })();

  const WishlistLabels = (() => {
    const cache = {};
    const SEL = '.wlc__card[data-wlc-handle], .wl-sl-card, .wl-pg-item';

    function load(handle) {
      if (!cache[handle]) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        cache[handle] = fetch(root + 'products/' + encodeURIComponent(handle) + '?view=hmlabels', { credentials: 'same-origin' })
          .then(r => (r.ok ? r.text() : ''))
          .then(html => {
            if (!html) return null;
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const pick = sel => Array.from(doc.querySelectorAll(sel + ' > .pcard-label:not(.pcard-label--ghost)'));
            return { above: pick('[data-hml-above]'), inside: pick('[data-hml-inside]') };
          })
          .catch(() => null);
      }
      return cache[handle];
    }

    function handleOf(card) {
      const h = card.getAttribute('data-wlc-handle');
      if (h) return h;
      const btn = card.querySelector('[data-wl-btn][data-handle]');
      if (btn) return btn.getAttribute('data-handle');
      const a = card.querySelector('a[href*="/products/"]');
      return a ? decodeURIComponent(a.getAttribute('href').split('?')[0].split('/products/')[1] || '') : '';
    }

    function hostOf(card) {
      return card.querySelector('.wlc__mediawrap, .card__inner, .wl-pg-item__img-wrap');
    }

    function hydrate(scope) {
      (scope || document).querySelectorAll(SEL).forEach(card => {
        if (card.hasAttribute('data-wl-lbl')) return;
        const handle = handleOf(card);
        const host = hostOf(card);
        if (!handle || !host) return;
        card.setAttribute('data-wl-lbl', '');
        load(handle).then(res => {
          if (!res || !host.isConnected) return;
          host.classList.add('wl-lbl-host');
          res.inside.forEach(el => host.appendChild(el.cloneNode(true)));
          res.above.forEach(el => host.insertBefore(el.cloneNode(true), host.firstChild));
        });
      });
    }

    let queued = false;
    function schedule() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; hydrate(); });
    }

    if ('MutationObserver' in window) {
      new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
    }

    return { hydrate, schedule };
  })();

  /* ─── Init ────────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    WishlistUI.init();
    WishlistPage.render();
    WishlistCartCarousel.render();
    WishlistPrice.hydrate();
    WishlistPrice.localize();
    WishlistLabels.hydrate();
  });

  // Expose for external use (e.g. carousel re-render after page change)
  window.WishlistUI      = WishlistUI;
  window.WishlistStorage = WishlistStorage;
  window.WishlistPage    = WishlistPage;
  window.WishlistCartCarousel = WishlistCartCarousel;
  window.WishlistPrice = WishlistPrice;
  /* Exposed so wishlist-page.js can reuse the drawer/bubble refresh instead of
     keeping a second copy of it. */
  window.WishlistCart    = { refresh: _refreshCartDrawer, add: _addToCartAjax };
}
